import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "state/hooks";
import {
  IntegratedAppSlice,
  IntegratedAppState,
  setIntegratedState,
  setSpeedIntegrationData,
} from "state/slices/integrated-app-slice";
import { getInitializeGamePath, getPrefixPath } from "utils/backend-path-builders";
import useQueryParams, { queryParamsToObject } from "utils/useQueryParams";
import { GROUP_SLUG } from "utils/constants";
import * as storage from "utils/local-storage";
import { getGroupState, setUserGroupMembershipStatus } from "state/slices/group-slice";
import { UserGroupMembershipStatus } from "utils/userGroupMembershipStatus";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserGroupSettingsByGroupId, UserGroupSettings } from "state/slices/user-settings-slice";
import { addGroupChangeRequestMessageListener } from "utils/integration/iframe-messages";
import { INTEGRATED_APP_HOME_PAGE_URL_PREFIX } from "utils/integration/constants";
import { setUserId } from "../state/slices/user-slice";
import { reportError } from "utils/report-error";

const InitializeIntegrationApp = (groupSlug: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const group = useAppSelector(getGroupState);
  const userGroupSettings = useAppSelector(state => getUserGroupSettingsByGroupId(state, group.id));
  const location = useLocation();

  const groupSettingsRef: React.MutableRefObject<{
    userGroupSettings: UserGroupSettings | undefined
  }> = useRef({ userGroupSettings });

  useEffect(() => {
    groupSettingsRef.current = { userGroupSettings };
  }, [userGroupSettings]);

  // Extract groupSlug from either pattern
  const pathParts = location.pathname.split("/");
  const effectiveGroupSlug = groupSlug || pathParts[pathParts.indexOf(INTEGRATED_APP_HOME_PAGE_URL_PREFIX) + 1];

  if (effectiveGroupSlug) {
    storage.session.setItem(GROUP_SLUG, effectiveGroupSlug);
  }

  // Check if we're in an integration app context (either pattern)
  const isIntegratedAppHomePage = location.pathname.includes(INTEGRATED_APP_HOME_PAGE_URL_PREFIX);

  const takeUserToTheIntegratedAppInitialPageIfNeeded = () => {
    const initialPage = queryParams.get("initialPage");
    if (initialPage) {
      navigate(initialPage);
    }
  };

  const sendInitializeGameRequest = async (
    externalLoginToken: string,
    externalUserId: string | null,
    extraPayload?: Record<string, unknown>
  ) => {
    const queryParamsJson = queryParamsToObject(queryParams);
    const body = extraPayload
      ? { ...queryParamsJson, ...extraPayload }
      : queryParamsJson;

    const res = await fetch(
      process.env.REACT_APP_BACKEND_URL +
      getPrefixPath() + "/" +
      effectiveGroupSlug +
      getInitializeGamePath(),
      {
        method: "POST",
        headers: {
          "x-user-id": externalUserId || "",
          "x-login-token": externalLoginToken,
        },
        body: JSON.stringify(body),
      }
    );

    const initializeGameResponse = await res.json().catch(() => ({}));

    // Only flip to INITIALIZED if the handshake actually succeeded AND returned
    // usable credentials. Without this guard a failed/partial init-game (bad
    // status, or a 200 with no token) would set state=INITIALIZED with empty
    // token/userId — every downstream integrated request then goes out with
    // empty auth headers and the user sees repeated "Unable to fetch balance".
    if (!res.ok || !(initializeGameResponse.token || initializeGameResponse.external_user_id)) {
      const initError: any = new Error(`initialize-game failed: HTTP ${res.status}`);
      initError.status = res.status;
      reportError(initError, "integration_handshake", {
        groupSlug: effectiveGroupSlug,
        hasToken: !!initializeGameResponse.token,
        hasExternalUserId: !!initializeGameResponse.external_user_id,
        responseBody: initializeGameResponse,
      });
      initError.sentryReported = true; // reported above; callers just toast
      throw initError;
    }

    dispatch(
      setIntegratedState({
        state: IntegratedAppState.INTEGRATED_SESSION_INITIALIZED,
        isIntegratedApp: true,
        userId: initializeGameResponse.external_user_id,
        token: initializeGameResponse.token,
        refreshToken: initializeGameResponse.refresh_token
      } as IntegratedAppSlice)
    );
    dispatch(setUserGroupMembershipStatus(UserGroupMembershipStatus.APPROVED));
    dispatch(setUserId(initializeGameResponse.user_id));

    // Backend echoes `lightning_address` on the Speed init-game response when
    // it has a stored value on user_external.wallet_address. Mirror it into
    // the Speed slice / sessionStorage so the withdrawal modal can prefill
    // even if Speed didn't send `p_add` in the Mini App launch URL this
    // session (e.g. warm-relaunch, browser reload, or Speed Dashboard not
    // configured to share the address).
    if (initializeGameResponse.lightning_address) {
      dispatch(setSpeedIntegrationData({
        lightningAddress: initializeGameResponse.lightning_address,
      }));
    }
  };

  useEffect(() => {
    if (!isIntegratedAppHomePage) {
      return;
    }

    // Speed Wallet integration: uses acct, lang, bal_btc, bal_usdt, p_add as URL params.
    // See: speed.md "Data Sharing Parameters for Mini Apps in Speed Wallet"
    const speedAccountId = queryParams.get("acct");
    if (speedAccountId) {
      dispatch(setIntegratedState({
        state: IntegratedAppState.INTEGRATED_SESSION_INITIALIZING,
        isIntegratedApp: true
      } as IntegratedAppSlice));

      const speedLang = queryParams.get("lang") || "";
      const speedBalBtc = Number(queryParams.get("bal_btc") || 0);
      const speedBalUsdt = Number(queryParams.get("bal_usdt") || 0);
      const speedLightningAddress = queryParams.get("p_add") || "";

      // Store Speed params in Redux so downstream components (deposit modal,
      // withdrawal modal, etc.) can read the Lightning address and balances.
      dispatch(setSpeedIntegrationData({
        accountId: speedAccountId,
        lightningAddress: speedLightningAddress,
        lang: speedLang,
        balanceBtc: speedBalBtc,
        balanceUsdt: speedBalUsdt,
      }));

      // Build the payload from Speed's data-sharing params
      const speedPayload = {
        // acct is used as the externalUserId header
        lang: speedLang,
        bal_btc: speedBalBtc,
        bal_usdt: speedBalUsdt,
        p_add: speedLightningAddress,
      };

      sendInitializeGameRequest("", speedAccountId, speedPayload)
        .then(() => takeUserToTheIntegratedAppInitialPageIfNeeded())
        .catch(e => {
          toast.error(`Something went wrong.`);
          reportError(e, "integration_handshake", { provider: "speed" });
        });
      return;
    }

    // Handle both parameter patterns for other providers
    const externalUserId = queryParams.get("user_id") || queryParams.get("playerId");
    const externalLoginToken = queryParams.get("login_token") || queryParams.get("loginToken");

    if (externalLoginToken) {
      dispatch(setIntegratedState({
        state: IntegratedAppState.INTEGRATED_SESSION_INITIALIZING,
        isIntegratedApp: true
      } as IntegratedAppSlice));

      sendInitializeGameRequest(externalLoginToken, externalUserId)
        .then(() => takeUserToTheIntegratedAppInitialPageIfNeeded())
        .catch(e => {
          toast.error(`Something went wrong.`);
          reportError(e, "integration_handshake", { provider: "login_token" });
        });
    } else {
      dispatch(setIntegratedState({
        state: IntegratedAppState.INTEGRATED_ANONYMOUS,
        isIntegratedApp: true
      } as IntegratedAppSlice));
      takeUserToTheIntegratedAppInitialPageIfNeeded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, queryParams, isIntegratedAppHomePage]);

  useEffect(() => {
    if (!isIntegratedAppHomePage) return;
    addGroupChangeRequestMessageListener(groupSettingsRef);
  }, [isIntegratedAppHomePage]);
};

export default InitializeIntegrationApp;
